import { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';

type Status = 'complete' | 'in-progress' | 'planned' | 'idea';

interface Milestone {
  title: string;
  description: string;
  status: Status;
  target?: string;
  tags?: string[];
}

interface Phase {
  name: string;
  subtitle: string;
  icon: string;
  milestones: Milestone[];
}

const STATUS_CONFIG: Record<Status, { label: string; color: string; dot: string }> = {
  complete: { label: 'Complete', color: 'text-green-700 bg-green-50 border-green-200', dot: 'bg-green-500' },
  'in-progress': { label: 'In Progress', color: 'text-primary bg-primary/10 border-primary/20', dot: 'bg-primary animate-pulse' },
  planned: { label: 'Planned', color: 'text-blue-700 bg-blue-50 border-blue-200', dot: 'bg-blue-400' },
  idea: { label: 'Idea', color: 'text-gray-500 bg-gray-50 border-gray-200', dot: 'bg-gray-300' },
};

const ROADMAP: Phase[] = [
  {
    name: 'Foundation',
    subtitle: 'Core business infrastructure',
    icon: 'foundation',
    milestones: [
      { title: 'Launch OchrePoint Platform', description: 'Core platform with jobs, scheduling, clients, and team management fully operational.', status: 'complete', target: 'Q1 2026', tags: ['Platform'] },
      { title: 'Client & Employee Portals', description: 'Separate portals for clients to view project updates and employees to manage schedules and timesheets.', status: 'complete', target: 'Q1 2026', tags: ['Portal'] },
      { title: 'Quote & Estimate Builder', description: 'Full quote builder with sections, line items, tax, and client delivery workflow.', status: 'complete', target: 'Q1 2026', tags: ['Finance'] },
      { title: 'Invoice & Payment Tracking', description: 'Invoice creation, payment recording, overdue tracking, and AR dashboard.', status: 'complete', target: 'Q1 2026', tags: ['Finance'] },
      { title: 'Payroll Module', description: 'Employee payroll processing with CPP, EI, and income tax calculations.', status: 'complete', target: 'Q2 2026', tags: ['Finance', 'HR'] },
    ],
  },
  {
    name: 'Growth',
    subtitle: 'Scale operations and revenue',
    icon: 'trending_up',
    milestones: [
      { title: 'QuickBooks Online Integration', description: 'Bi-directional sync of clients, invoices, and payments with QuickBooks Online.', status: 'in-progress', target: 'Q2 2026', tags: ['Integration', 'Finance'] },
      { title: 'Online Payment Processing', description: 'Accept credit card payments directly through client invoices via Stripe.', status: 'in-progress', target: 'Q3 2026', tags: ['Finance'] },
      { title: 'Client Self-Service Approvals', description: 'Clients can approve change orders and make payments directly through their portal. Token-based email approvals also supported.', status: 'complete', target: 'Q3 2026', tags: ['Portal', 'Client Experience'] },
      { title: 'Crew GPS & Time Tracking', description: 'Mobile time clock for field crews. Auto-populate timesheets via employee portal.', status: 'complete', target: 'Q3 2026', tags: ['Field Ops', 'HR'] },
      { title: 'Automated Invoice Reminders', description: 'Auto-send payment reminders to clients at 7, 14, and 30 days overdue. Admin overdue alerts are live; timed client-facing emails in progress.', status: 'in-progress', target: 'Q3 2026', tags: ['Finance', 'Automation'] },
      { title: 'Subcontractor Management', description: 'Track subcontractor WCB clearance, liability insurance, roles, and compliance alerts.', status: 'complete', target: 'Q4 2026', tags: ['HR', 'Finance'] },
      { title: 'SMS & Email Crew Notifications', description: 'Crew members receive automatic SMS or email when scheduled for a job. Twilio integration active with opt-in preferences per employee.', status: 'complete', target: 'Q3 2026', tags: ['Field Ops', 'Automation'] },
    ],
  },
  {
    name: 'Excellence',
    subtitle: 'Operational excellence and scale',
    icon: 'workspace_premium',
    milestones: [
      { title: 'Mobile App (iOS & Android)', description: 'Native mobile app for field crews to log time, view schedules, and upload job photos from the site.', status: 'planned', target: 'Q1 2027', tags: ['Mobile', 'Field Ops'] },
      { title: 'Bid & Tender Management', description: 'Quote-based bid workflow with win/loss report: track win rate, revenue won/lost, and per-client performance.', status: 'complete', target: 'Q1 2027', tags: ['Business Dev'] },
      { title: 'Materials Procurement', description: 'Materials catalog with supplier import, job cart, and cost-to-estimate integration.', status: 'complete', target: 'Q2 2027', tags: ['Operations'] },
      { title: 'Job Cost Analysis', description: 'Compare estimated vs. actual costs by job. Profitability report with CSV/PDF export.', status: 'complete', target: 'Q2 2027', tags: ['Finance', 'Analytics'] },
      { title: 'Crew Training & Certifications', description: 'Track skills, tickets, licenses, and certifications per employee with expiry alerts.', status: 'complete', target: 'Q2 2027', tags: ['HR', 'Safety'] },
      { title: 'Work Agreement & Contract Builder', description: 'Generate branded work agreements with scope, payment schedule, milestones, and signature blocks. Quote-to-agreement workflow with live preview and print/export.', status: 'complete', target: 'Q2 2027', tags: ['Finance', 'Operations'] },
      { title: 'Job Labor Cost Estimation', description: 'Assign crews or individual employees to jobs with estimated hours and rate overrides. Auto-calculates estimated labor cost per job using effective pay rates.', status: 'complete', target: 'Q2 2027', tags: ['Finance', 'Field Ops'] },
      { title: 'Multi-Location Support', description: 'Manage multiple office locations, crews, and accounting entities from one platform.', status: 'idea', tags: ['Platform'] },
      { title: 'AI-Powered Estimating', description: 'Use historical job data to generate smarter estimates. AI assistant available for drafting messages, material suggestions, schedule optimization, and quote review.', status: 'in-progress', tags: ['AI', 'Finance'] },
    ],
  },
  {
    name: 'Community',
    subtitle: 'Giving back and building partnerships',
    icon: 'favorite',
    milestones: [
      { title: 'Apprenticeship Program', description: 'Formalize an apprenticeship program to develop local carpentry talent and give back to the North Cowichan community.', status: 'planned', target: 'Q4 2026', tags: ['Community', 'HR'] },
      { title: 'Local Supplier Partnerships', description: 'Preferred supplier agreements with local Duncan-area suppliers for better pricing and community ties.', status: 'complete', target: 'Q2 2026', tags: ['Community', 'Procurement'] },
      { title: 'Charitable Build Projects', description: 'Annual pro-bono project for local non-profit or community organization.', status: 'planned', target: '2027', tags: ['Community'] },
    ],
  },
];

export const RoadmapPage = () => {
  const [filter, setFilter] = useState<Status | 'all'>('all');

  const allStatuses: Status[] = ['complete', 'in-progress', 'planned', 'idea'];
  const totalComplete = ROADMAP.flatMap(p => p.milestones).filter(m => m.status === 'complete').length;
  const totalMilestones = ROADMAP.flatMap(p => p.milestones).length;

  return (
    <MainLayout>
      <div className="flex flex-col min-h-screen">
        <header className="sticky top-14 md:top-0 z-20 border-b border-gray-200 px-4 py-4 md:px-8 md:py-5 bg-gray-50/80 backdrop-blur-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 rounded-md p-2">
                <span className="material-symbols-outlined text-primary text-2xl">map</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Company Roadmap</h2>
                <p className="text-sm text-gray-500">OchrePoint Contracting — growth plan & milestones</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-primary">{totalComplete}/{totalMilestones}</p>
              <p className="text-xs text-gray-500">milestones complete</p>
            </div>
          </div>
        </header>

        <div className="p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            {/* Vision statement */}
            <div className="mb-8 p-6 bg-gradient-to-r from-primary/10 to-amber-50 border border-primary/20 rounded-2xl">
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Our Vision</p>
              <p className="text-gray-800 font-medium leading-relaxed">
                "Built on Skill, Finished with Pride" — To be the most trusted general contractor in the Cowichan Valley,
                known for exceptional craftsmanship, reliable scheduling, and transparent communication with every client.
              </p>
            </div>

            {/* Filter */}
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
              >
                All
              </button>
              {allStatuses.map(s => {
                const cfg = STATUS_CONFIG[s];
                return (
                  <button
                    key={s}
                    onClick={() => setFilter(filter === s ? 'all' : s)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filter === s ? cfg.color : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
                  >
                    <span className={`size-2 rounded-full ${cfg.dot}`} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>

            {/* Phases */}
            <div className="space-y-10">
              {ROADMAP.map((phase, pi) => {
                const filtered = filter === 'all' ? phase.milestones : phase.milestones.filter(m => m.status === filter);
                if (filtered.length === 0) return null;
                const phaseComplete = phase.milestones.filter(m => m.status === 'complete').length;
                return (
                  <div key={pi}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-white border-2 border-primary/20 rounded-xl p-2.5">
                        <span className="material-symbols-outlined text-primary text-xl">{phase.icon}</span>
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-gray-900">{phase.name}</h3>
                        <p className="text-sm text-gray-500">{phase.subtitle} · {phaseComplete}/{phase.milestones.length} complete</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {filtered.map((m, mi) => {
                        const cfg = STATUS_CONFIG[m.status];
                        return (
                          <div key={mi} className="bg-white rounded-xl border border-gray-200 p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className={`text-sm font-semibold ${m.status === 'complete' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{m.title}</p>
                              <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full border font-medium ${cfg.color}`}>{cfg.label}</span>
                            </div>
                            <p className="text-xs text-gray-500 leading-relaxed mb-3">{m.description}</p>
                            <div className="flex items-center justify-between">
                              <div className="flex flex-wrap gap-1">
                                {m.tags?.map(tag => (
                                  <span key={tag} className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{tag}</span>
                                ))}
                              </div>
                              {m.target && <span className="text-xs text-gray-400">{m.target}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
